import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { SDialog } from '@modules/ui-components';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import {
  BRAND_STYLE_REFERENCE_LIMIT,
  BrandStyleKitEditor,
  DESIGN_PROFILE_OPTIONS,
  brandStyleKitDraftToPatch,
  emptyBrandStyleKitDraft,
  getDesignProfileOption,
  inferBrandStyleKitMimeType,
  profileToBrandStyleKitDraft,
  type BrandStyleKitDraft,
} from '@modules/content-plan/components/design/BrandStyleKitEditor';
import { usePlatformDesign } from '@modules/content-plan/hooks/usePlatformDesign';
import {
  buildExportFileName,
  exportDesignSystem,
  importDesignSystem,
  type ImportDesignSystemResult,
} from '@modules/content-plan/components/design/designSystemImportExport';
import {
  getAuthorPlatformService,
  type AuthorBasePath,
  type AuthorDesignProfile,
  type AuthorDesignProfileSet,
  type AuthorDesignSystemPackage,
  type AuthorDesignSystemPackageProfile,
  type AuthorDesignSystemPackageProfileInput,
} from '@modules/content-plan/services/AuthorPlatformService';
import {
  systemPlatformService,
  type SystemBrandStyleKitProfile,
} from '@modules/content-plan/services/SystemPlatformService';
import type { PlatformDesignProfile } from '@modules/content-plan/types';
import { ApplyBaseColorsDialog } from '@modules/content-plan/components/design/ApplyBaseColorsDialog';

type VisualReference = NonNullable<PlatformDesignProfile['visualReferences']>[number];

function stripPackageHashMetadata<T extends Record<string, unknown>>(profile: T): T {
  const {
    contentHash: _contentHash,
    designPackageContentHash: _designPackageContentHash,
    designPackageProfileContentHash: _designPackageProfileContentHash,
    ...cleanProfile
  } = profile;
  return cleanProfile as T;
}

function isLegacyDesignReferenceUrl(url: string): boolean {
  return url.includes('/design_profile_sets/');
}

function packageVisualReferences(
  profile: Pick<PlatformDesignProfile, 'visualReferences'> | null | undefined,
): VisualReference[] {
  return (profile?.visualReferences ?? [])
    .filter((ref): ref is VisualReference => {
      const url = ref.url?.trim();
      return Boolean(url) && !isLegacyDesignReferenceUrl(url);
    });
}

function sanitizePackageProfileReferences<T extends { visualReferences?: PlatformDesignProfile['visualReferences'] }>(
  profile: T,
): T {
  return {
    ...profile,
    visualReferences: packageVisualReferences(profile),
  };
}

function mergePackageProfileIntoLegacyProfiles(
  packageProfile: AuthorDesignSystemPackageProfile | null,
  legacyProfiles: AuthorDesignProfile[],
): AuthorDesignProfile[] {
  if (!packageProfile) return legacyProfiles;
  const packageProfileAsAuthor = packageProfile as AuthorDesignProfile;
  return [
    packageProfileAsAuthor,
    ...legacyProfiles.filter((profile) => profile.id !== packageProfileAsAuthor.id),
  ];
}

type DesignSlotPreviewGeometry = {
  aspectRatio: string;
  label: string;
  width: number;
  miniWidth: number;
};

const DESIGN_SLOT_PREVIEW_GEOMETRY: Record<string, DesignSlotPreviewGeometry> = {
  design: { aspectRatio: '4 / 5', label: 'shared', width: 120, miniWidth: 28 },
  single_image_landscape: { aspectRatio: '1.91 / 1', label: '1.91:1', width: 220, miniWidth: 44 },
  single_image_square: { aspectRatio: '1 / 1', label: '1:1', width: 132, miniWidth: 30 },
  single_image_portrait: { aspectRatio: '4 / 5', label: '4:5', width: 120, miniWidth: 28 },
  carousel_square: { aspectRatio: '1 / 1', label: '1:1', width: 132, miniWidth: 30 },
  carousel_portrait: { aspectRatio: '4 / 5', label: '4:5', width: 120, miniWidth: 28 },
};

function designSlotPreviewGeometry(profileKey: string): DesignSlotPreviewGeometry {
  return DESIGN_SLOT_PREVIEW_GEOMETRY[profileKey] ?? DESIGN_SLOT_PREVIEW_GEOMETRY.design;
}

interface Props {
  projectId: string;
  authorId: string;
  platformId: string;
  basePath?: AuthorBasePath;
}

export const PlatformDesignEditor: React.FC<Props> = ({
  projectId,
  authorId,
  platformId,
  basePath = 'authors',
}) => {
  const [profileKey, setProfileKey] = useState('design');
  const authorService = useMemo(() => getAuthorPlatformService(basePath), [basePath]);
  const { design, loading, error } = usePlatformDesign(projectId, authorId, platformId, basePath, profileKey);
  const [draft, setDraft] = useState<BrandStyleKitDraft>(() => emptyBrandStyleKitDraft());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploadingRefIndex, setUploadingRefIndex] = useState<number | null>(null);
  const [librarySets, setLibrarySets] = useState<AuthorDesignProfileSet[]>([]);
  const [designPackages, setDesignPackages] = useState<AuthorDesignSystemPackage[]>([]);
  const [authorProfiles, setAuthorProfiles] = useState<AuthorDesignProfile[]>([]);
  const [catalogProfiles, setCatalogProfiles] = useState<SystemBrandStyleKitProfile[]>([]);
  const [catalogRowSelection, setCatalogRowSelection] = useState<SDataTableRowSelectionState>({});
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState('');
  const [editingProfileName, setEditingProfileName] = useState('');
  const [editingProfileDescription, setEditingProfileDescription] = useState('');
  const [hydratedEditorKey, setHydratedEditorKey] = useState('');
  const [baseColorsOpen, setBaseColorsOpen] = useState(false);
  const [baseColorsApplying, setBaseColorsApplying] = useState(false);

  // Import/Export state
  const [importAnchor, setImportAnchor] = useState<HTMLButtonElement | null>(null);
  const [transferMessage, setTransferMessage] = useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reloadLibrary = useCallback(async () => {
    setLoadingLibrary(true);
    setSaveError(null);
    try {
      const [sets, packages] = await Promise.all([
        authorService.listDesignProfileSets({
          projectId,
          authorId,
          platformId,
          includeProfiles: true,
        }),
        authorService.listDesignSystemPackages({
          projectId,
          authorId,
          platformId,
          includeProfiles: true,
        }),
      ]);
      setLibrarySets(sets);
      setDesignPackages(packages);
      setAuthorProfiles(sets.find((set) => String(set.profileKey) === profileKey)?.profiles ?? []);
    } catch (e) {
      setLibrarySets([]);
      setDesignPackages([]);
      setAuthorProfiles([]);
      setSaveError((e as Error).message);
    } finally {
      setLoadingLibrary(false);
    }
  }, [authorId, authorService, platformId, profileKey, projectId]);

  useEffect(() => {
    void reloadLibrary();
  }, [reloadLibrary]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCatalog(true);
    setCatalogRowSelection({});
    systemPlatformService.listBrandStyleKitProfiles(platformId, profileKey)
      .then((profiles) => {
        if (cancelled) return;
        setCatalogProfiles(profiles);
      })
      .catch(() => {
        if (!cancelled) setCatalogProfiles([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
  }, [platformId, profileKey]);

  const currentSet = useMemo(
    () => librarySets.find((set) => String(set.profileKey) === profileKey),
    [librarySets, profileKey],
  );
  const currentProfileId = String(currentSet?.currentProfileId ?? '').trim();
  const currentDesignPackage = useMemo(
    () => designPackages.find((item) => item.current) ?? designPackages[0] ?? null,
    [designPackages],
  );
  const currentPackageProfile = useMemo(
    () => currentDesignPackage?.profiles?.find((profile) => String(profile.profileKey) === profileKey) ?? null,
    [currentDesignPackage, profileKey],
  );
  const legacyCurrentProfile = useMemo(
    () => authorProfiles.find((profile) => profile.id === currentProfileId) ?? authorProfiles[0] ?? null,
    [authorProfiles, currentProfileId],
  );
  const currentProfile = useMemo(
    () => (currentPackageProfile as AuthorDesignProfile | null) ?? (currentDesignPackage ? null : legacyCurrentProfile),
    [currentDesignPackage, currentPackageProfile, legacyCurrentProfile],
  );
  const visibleAuthorProfiles = useMemo(
    () => (currentDesignPackage
      ? (currentPackageProfile ? [currentPackageProfile as AuthorDesignProfile] : [])
      : mergePackageProfileIntoLegacyProfiles(currentPackageProfile, authorProfiles)),
    [authorProfiles, currentDesignPackage, currentPackageProfile],
  );
  const foundationReferences = useMemo(
    () => packageVisualReferences(
      currentDesignPackage?.profiles?.find((profile) => String(profile.profileKey) === 'design'),
    ),
    [currentDesignPackage],
  );
  const foundationReferenceUrls = useMemo(
    () => foundationReferences.map((ref) => ref.url.trim()).filter(Boolean),
    [foundationReferences],
  );
  const profileWithEditableInheritedReferences = useCallback((profile: AuthorDesignProfile | null): AuthorDesignProfile | null => {
    if (!profile || !currentDesignPackage || profileKey === 'design') return profile;
    if (packageVisualReferences(profile).length > 0 || foundationReferences.length === 0) return profile;
    return {
      ...profile,
      visualReferences: foundationReferences,
    };
  }, [currentDesignPackage, foundationReferences, profileKey]);
  const savedReferenceCount = useMemo(
    () => packageVisualReferences(currentProfile ?? (!currentDesignPackage ? design : null)).length,
    [currentDesignPackage, currentProfile, design],
  );
  const hasReferenceOverflow = savedReferenceCount > BRAND_STYLE_REFERENCE_LIMIT;
  const profileOption = getDesignProfileOption(profileKey);

  const openEditor = (profile?: AuthorDesignProfile | null) => {
    const target = profileWithEditableInheritedReferences(profile
      ?? currentProfile
      ?? (!currentDesignPackage && design ? ({ ...design, id: `${profileKey}_manual_v1` } as AuthorDesignProfile) : null));
    const profileId = target?.id ?? target?.profileId ?? `${profileKey}_manual_v1`;
    setEditingProfileId(String(profileId));
    setEditingProfileName(String(target?.name ?? `${profileOption.label} profile`));
    setEditingProfileDescription(String(target?.description ?? ''));
    setDraft(profileToBrandStyleKitDraft(target));
    setHydratedEditorKey(`${profileKey}:${profileId}`);
    setEditorOpen(true);
  };

  const openSlotEditor = (nextProfileKey: string) => {
    setProfileKey(nextProfileKey);
    setHydratedEditorKey('');
    setEditingProfileId(`${nextProfileKey}_manual_v1`);
    setEditingProfileName(`${getDesignProfileOption(nextProfileKey).label} profile`);
    setEditingProfileDescription('');
    setDraft(emptyBrandStyleKitDraft());
    setEditorOpen(true);
  };

  useEffect(() => {
    if (!editorOpen || loadingLibrary) return;
    const nextProfileOption = getDesignProfileOption(profileKey);
    const target = profileWithEditableInheritedReferences(currentProfile
      ?? (!currentDesignPackage && design ? ({ ...design, id: `${profileKey}_manual_v1` } as AuthorDesignProfile) : null));
    const profileId = String(target?.id ?? target?.profileId ?? `${profileKey}_manual_v1`);
    const inheritedSignature = packageVisualReferences(target).length > 0
      ? packageVisualReferences(target).map((ref) => ref.url).join('|')
      : '';
    const nextHydrationKey = `${profileKey}:${profileId}:${inheritedSignature}`;
    if (hydratedEditorKey === nextHydrationKey) return;
    setEditingProfileId(profileId);
    setEditingProfileName(String(target?.name ?? `${nextProfileOption.label} profile`));
    setEditingProfileDescription(String(target?.description ?? ''));
    setDraft(profileToBrandStyleKitDraft(target));
    setHydratedEditorKey(nextHydrationKey);
  }, [currentDesignPackage, currentProfile, design, editorOpen, hydratedEditorKey, loadingLibrary, profileKey, profileWithEditableInheritedReferences]);

  const patchReference = (index: number, patch: Partial<BrandStyleKitDraft['references'][number]>) => {
    setDraft((prev) => ({
      ...prev,
      references: prev.references.map((ref, refIndex) => (
        refIndex === index ? { ...ref, ...patch } : ref
      )),
    }));
  };

  const removeReference = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      references: prev.references.filter((_, refIndex) => refIndex !== index),
    }));
  };

  const uploadReferenceFile = async (file: File): Promise<{ url: string; mimeType: string }> => {
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const packageId = currentDesignPackage?.packageId || 'manual-package-v1';
    const path = `projects/${projectId}/${basePath}/${authorId}/platforms/${platformId}/design_system_packages/${packageId}/profiles/${profileKey}/references/${Date.now()}_${sanitizedName}`;
    const storage = new StorageService();
    const result = await storage.uploadImage(path, file);
    return { url: result.assetUrl.origin, mimeType: file.type || inferBrandStyleKitMimeType(result.assetUrl.origin) };
  };

  const handleReferenceMediaChange = async (index: number, value: string | File | null) => {
    setSaveError(null);
    if (value instanceof File) {
      setUploadingRefIndex(index);
      try {
        const uploaded = await uploadReferenceFile(value);
        patchReference(index, uploaded);
      } catch (e) {
        setSaveError((e as Error).message);
      } finally {
        setUploadingRefIndex(null);
      }
      return;
    }
    if (typeof value === 'string') {
      const url = value.trim();
      patchReference(index, {
        url,
        mimeType: url ? inferBrandStyleKitMimeType(url) : 'image/png',
      });
      return;
    }
    removeReference(index);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const profileId = editingProfileId.trim() || `${profileKey}_manual_v1`;
      const existing = visibleAuthorProfiles.find((profile) => {
        const candidateProfileKey = String((profile as Record<string, unknown>).profileKey ?? profileKey);
        return candidateProfileKey === profileKey && (
          profile.id === profileId || String(profile.profileId ?? '') === profileId
        );
      });
      const patch = brandStyleKitDraftToPatch(platformId, draft, existing?.source ?? 'manual');
      const packageId = currentDesignPackage?.packageId || 'manual-package-v1';
      const nextProfilesByKey = new Map<string, AuthorDesignSystemPackageProfileInput>();
      const packageProfiles = currentDesignPackage?.profiles ?? [];
      packageProfiles.forEach((profile) => nextProfilesByKey.set(String(profile.profileKey), {
        ...sanitizePackageProfileReferences(stripPackageHashMetadata(profile as AuthorDesignSystemPackageProfileInput & Record<string, unknown>)),
        id: String(profile.profileKey),
        packageId,
        profileId: packageId,
      }));
      const cleanExisting = existing
        ? stripPackageHashMetadata(existing as AuthorDesignProfile & Record<string, unknown>)
        : undefined;
      const nextProfile = sanitizePackageProfileReferences({
        projectId,
        ...(cleanExisting ?? {}),
        ...(patch as PlatformDesignProfile),
        id: profileKey,
        packageId,
        profileId: packageId,
        profileKey,
        name: editingProfileName.trim() || `${profileOption.label} profile`,
        description: editingProfileDescription.trim() || undefined,
        platformId,
        authorId,
        source: 'package_profile',
        isComplete: draft.isComplete,
      } as unknown as AuthorDesignSystemPackageProfileInput);
      nextProfilesByKey.set(profileKey, nextProfile);
      await authorService.saveDesignSystemPackage({
        projectId,
        authorId,
        platformId,
        designPackage: {
          packageId,
          name: currentDesignPackage?.name ?? `${platformId} design package`,
          description: currentDesignPackage?.description ?? 'Author platform design package.',
          status: currentDesignPackage?.status ?? 'draft',
          visualFoundation: currentDesignPackage?.visualFoundation,
          targetProfileKeys: DESIGN_PROFILE_OPTIONS.map((option) => option.value),
          profiles: Array.from(nextProfilesByKey.values()),
        },
        setCurrent: true,
      });
      await reloadLibrary();
      setEditorOpen(false);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSelectedCatalogProfiles = async () => {
    const profileIds = Object.entries(catalogRowSelection)
      .filter(([, selected]) => Boolean(selected))
      .map(([id]) => id);
    if (profileIds.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      await authorService.copySystemDesignProfilesToAuthor({
        projectId,
        authorId,
        platformId,
        profileKey,
        profileIds,
      });
      await reloadLibrary();
      setPickerOpen(false);
      setCatalogRowSelection({});
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleExportDesignSystem = useCallback(async () => {
    try {
      const payload = exportDesignSystem(draft);
      const json = JSON.stringify(payload, null, 2);
      const fileName = buildExportFileName(platformId, profileKey);

      let clipboardOk = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(json);
          clipboardOk = true;
        }
      } catch {
        // Clipboard write can be blocked by permissions; the file download below still works.
      }

      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);

      setTransferMessage({
        severity: 'success',
        text: clipboardOk
          ? `Exported to ${fileName} and copied JSON to clipboard.`
          : `Exported to ${fileName}. Clipboard copy was blocked by the browser.`,
      });
    } catch (e) {
      setTransferMessage({ severity: 'error', text: `Export failed: ${(e as Error).message}` });
    }
  }, [draft, platformId, profileKey]);

  const applyImportedPatch = useCallback((rawText: string) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawText);
    } catch (e) {
      setTransferMessage({ severity: 'error', text: `Import failed: invalid JSON — ${(e as Error).message}` });
      return;
    }
    const result: ImportDesignSystemResult = importDesignSystem(parsed);
    if (result.ok !== true) {
      setTransferMessage({ severity: 'error', text: `Import failed: ${result.error}` });
      return;
    }
    setDraft((prev) => ({ ...prev, ...result.draft }));
    const baseText = 'Imported prompt-relevant fields. Press Save profile to persist.';
    setTransferMessage({
      severity: result.warnings.length > 0 ? 'warning' : 'success',
      text: result.warnings.length > 0 ? `${baseText} Notes: ${result.warnings.join(' ')}` : baseText,
    });
  }, []);

  const handleImportFromClipboard = useCallback(async () => {
    setImportAnchor(null);
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
        setTransferMessage({ severity: 'error', text: 'Clipboard read is not available in this browser.' });
        return;
      }
      const text = await navigator.clipboard.readText();
      if (!text.trim()) {
        setTransferMessage({ severity: 'error', text: 'Clipboard is empty.' });
        return;
      }
      applyImportedPatch(text);
    } catch (e) {
      setTransferMessage({ severity: 'error', text: `Clipboard read blocked: ${(e as Error).message}` });
    }
  }, [applyImportedPatch]);

  const handleImportFromFileClick = useCallback(() => {
    setImportAnchor(null);
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file fires onChange again.
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      applyImportedPatch(text);
    };
    reader.onerror = () => {
      setTransferMessage({ severity: 'error', text: `File read failed: ${reader.error?.message ?? 'unknown error'}` });
    };
    reader.readAsText(file);
  }, [applyImportedPatch]);

  const handleMakeCurrent = async (profileId: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await authorService.makeDesignProfileCurrent({
        projectId,
        authorId,
        platformId,
        profileKey,
        profileId,
      });
      await reloadLibrary();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleMakePackageCurrent = async (packageId: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await authorService.makeDesignSystemPackageCurrent({
        projectId,
        authorId,
        platformId,
        packageId,
      });
      await reloadLibrary();
      setTransferMessage({
        severity: 'success',
        text: 'Design package made current for all included post types.',
      });
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyBaseColors = async (targetProfileKeys: string[]) => {
    setBaseColorsApplying(true);
    setSaveError(null);
    try {
      await authorService.applyBaseColorsToPostTypes({
        projectId,
        authorId,
        platformId,
        targetProfileKeys,
      });
      await reloadLibrary();
      setBaseColorsOpen(false);
      setTransferMessage({
        severity: 'success',
        text: 'Base colors applied to selected post-type design profiles.',
      });
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setBaseColorsApplying(false);
    }
  };

  const authorProfileRows = useMemo<AuthorProfileTableRow[]>(() => (
    visibleAuthorProfiles.map((profile) => ({
      id: profile.id,
      scope: profileOption.label,
      name: profile.name ?? profile.id,
      description: profile.description ?? '',
      current: profile.id === currentProfileId || profile.id === currentPackageProfile?.id,
      references: profile.visualReferences?.length ?? 0,
      reviewed: profile.isComplete === true,
      sourceProfile: profile,
    }))
  ), [currentPackageProfile?.id, currentProfileId, profileOption.label, visibleAuthorProfiles]);

  const authorProfileColumns = useMemo<SDataTableColumnDef<AuthorProfileTableRow>[]>(() => [
    { accessorKey: 'scope', header: 'Scope' },
    { accessorKey: 'name', header: 'Profile' },
    { accessorKey: 'description', header: 'Description' },
    {
      accessorKey: 'current',
      header: 'Current',
      Cell: ({ cell }: any) => (
        cell.getValue()
          ? <Chip size="small" color="success" label="Current" />
          : <Typography variant="caption" color="text.secondary">Variant</Typography>
      ),
    },
    { accessorKey: 'references', header: 'Refs' },
    {
      accessorKey: 'reviewed',
      header: 'Reviewed',
      Cell: ({ cell }: any) => (
        <Typography variant="caption" color={cell.getValue() ? 'success.main' : 'text.secondary'}>
          {cell.getValue() ? 'Yes' : 'No'}
        </Typography>
      ),
    },
  ], []);

  const catalogProfileColumns = useMemo<SDataTableColumnDef<SystemBrandStyleKitProfileRow>[]>(() => [
    { accessorKey: 'name', header: 'Profile' },
    { accessorKey: 'description', header: 'Description' },
    { accessorKey: 'references', header: 'Refs' },
    {
      accessorKey: 'reviewed',
      header: 'Reviewed',
      Cell: ({ cell }: any) => (
        <Typography variant="caption" color={cell.getValue() ? 'success.main' : 'text.secondary'}>
          {cell.getValue() ? 'Yes' : 'No'}
        </Typography>
      ),
    },
  ], []);

  const catalogProfileRows = useMemo<SystemBrandStyleKitProfileRow[]>(() => (
    catalogProfiles.map((profile) => ({
      id: profile.id,
      name: profile.name ?? profile.id,
      description: profile.description ?? '',
      references: profile.visualReferences?.length ?? 0,
      reviewed: profile.isComplete === true,
      sourceProfile: profile,
    }))
  ), [catalogProfiles]);

  const slotRows = useMemo<DesignSlotRow[]>(() => (
    DESIGN_PROFILE_OPTIONS.map((option) => {
      const geometry = designSlotPreviewGeometry(option.value);
      const set = librarySets.find((item) => String(item.profileKey) === option.value);
      const profiles = set?.profiles ?? [];
      const currentId = String(set?.currentProfileId ?? '').trim();
      const packageProfile = currentDesignPackage?.profiles?.find((profile) => String(profile.profileKey) === option.value) ?? null;
      const legacyCurrent = currentDesignPackage ? null : profiles.find((profile) => profile.id === currentId) ?? profiles[0] ?? null;
      const current = packageProfile ?? legacyCurrent;
      const runtimeCurrent = !currentDesignPackage && option.value === profileKey ? design : null;
      const directReferenceUrls = packageVisualReferences(current ?? runtimeCurrent)
        .map((ref) => ref.url.trim());
      const referenceUrls = directReferenceUrls.length > 0 ? directReferenceUrls : foundationReferenceUrls;
      return {
        id: option.value,
        label: option.label,
        helper: option.helper,
        currentName: current?.name ?? current?.id ?? '',
        description: current?.description ?? current?.designDescription ?? runtimeCurrent?.designDescription ?? '',
        current: Boolean(current),
        references: directReferenceUrls.length,
        inheritedReferences: directReferenceUrls.length > 0 ? 0 : foundationReferenceUrls.length,
        referencePreviewUrls: referenceUrls.slice(0, 3),
        previewAspectRatio: geometry.aspectRatio,
        previewAspectLabel: geometry.label,
        previewWidth: geometry.width,
        previewMiniWidth: geometry.miniWidth,
        reviewed: current?.isComplete === true,
        profileCount: packageProfile ? Math.max(profiles.length, 1) : profiles.length,
      };
    })
  ), [currentDesignPackage, design, foundationReferenceUrls, librarySets, profileKey]);

  if (loading || loadingLibrary) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading design profile...</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} data-testid={`platform-${platformId}-design-editor`}>
      {error && <Alert severity="error">{error.message}</Alert>}
      {saveError && <Alert severity="error">{saveError}</Alert>}
      {hasReferenceOverflow && (
        <Alert severity="warning">
          The design resolver sends only the first {BRAND_STYLE_REFERENCE_LIMIT} references to image providers.
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          Design system package
        </Typography>
        <Typography variant="body2" color="text.secondary">
          One package owns the platform visual language and adapts it across every supported post type.
        </Typography>
      </Box>

      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'background.paper',
        }}
        data-testid={`platform-${platformId}-design-package-summary`}
      >
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2">
                {currentDesignPackage?.name ?? 'No package yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentDesignPackage
                  ? currentDesignPackage.description || `Package id: ${currentDesignPackage.packageId}`
                  : 'Saving any post-type slot will create the first author design package.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color={currentDesignPackage ? 'success' : 'warning'}
                label={currentDesignPackage ? 'Current package' : 'Empty'}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${currentDesignPackage?.profiles?.length ?? 0}/${DESIGN_PROFILE_OPTIONS.length} slots`}
              />
              {currentDesignPackage?.status && (
                <Chip size="small" variant="outlined" label={currentDesignPackage.status} />
              )}
            </Stack>
          </Stack>
          {designPackages.length > 1 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {designPackages.map((designPackage) => (
                <Button
                  key={designPackage.packageId}
                  size="small"
                  variant={designPackage.current ? 'contained' : 'outlined'}
                  startIcon={designPackage.current ? <CheckIcon /> : undefined}
                  onClick={() => {
                    if (!designPackage.current) void handleMakePackageCurrent(designPackage.packageId);
                  }}
                  disabled={saving || designPackage.current}
                >
                  {designPackage.name ?? designPackage.packageId}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 1.5,
        }}
        data-testid={`platform-${platformId}-design-slot-matrix`}
      >
        {slotRows.map((slot) => (
          <Box
            key={slot.id}
            role="button"
            tabIndex={0}
            onClick={() => openSlotEditor(slot.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openSlotEditor(slot.id);
              }
            }}
            data-testid={`platform-${platformId}-design-slot-${slot.id}`}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
              minHeight: 240,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
              '&:focus-visible': {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2">{slot.label}</Typography>
                <Typography variant="caption" color="text.secondary">{slot.helper}</Typography>
              </Box>
              <Chip
                size="small"
                color={slot.reviewed ? 'success' : slot.current ? 'default' : 'warning'}
                label={slot.reviewed ? 'Reviewed' : slot.current ? 'Draft' : 'Empty'}
                variant={slot.current ? 'filled' : 'outlined'}
              />
            </Stack>
            <Box
              data-testid={`platform-${platformId}-design-slot-${slot.id}-refs`}
              sx={{
                position: 'relative',
                width: `min(100%, ${slot.previewWidth}px)`,
                mx: 'auto',
                aspectRatio: slot.previewAspectRatio,
                boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
                borderRadius: 0.75,
                bgcolor: 'background.default',
                overflow: 'hidden',
              }}
            >
              <Chip
                size="small"
                label={slot.previewAspectLabel}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  zIndex: 2,
                  height: 20,
                  fontSize: '0.66rem',
                  bgcolor: 'rgba(9, 7, 22, 0.72)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              />
              {slot.referencePreviewUrls.length > 0 ? (
                <>
                  <Box
                    component="img"
                    src={slot.referencePreviewUrls[0]}
                    alt={`${slot.label} reference 1`}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      bgcolor: 'background.default',
                    }}
                  />
                  {slot.referencePreviewUrls.length > 1 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        bottom: 8,
                        zIndex: 2,
                        maxWidth: 'calc(100% - 16px)',
                        overflow: 'hidden',
                      }}
                    >
                      {slot.referencePreviewUrls.slice(1).map((url, index) => (
                        <Box
                          key={`${slot.id}-secondary-ref-${index}-${url}`}
                          sx={{
                            width: slot.previewMiniWidth,
                            aspectRatio: slot.previewAspectRatio,
                            borderRadius: 0.5,
                            bgcolor: 'background.default',
                            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.28), 0 4px 14px rgba(0, 0, 0, 0.38)',
                            overflow: 'hidden',
                            flex: '0 0 auto',
                            boxSizing: 'border-box',
                          }}
                        >
                          <Box
                            component="img"
                            src={url}
                            alt={`${slot.label} reference ${index + 2}`}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    borderRadius: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">No refs yet</Typography>
                </Box>
              )}
            </Box>
            <Typography
              variant="body2"
              color={slot.current ? 'text.secondary' : 'warning.main'}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {slot.current
                ? slot.description || slot.currentName || 'Profile has structured style settings.'
                : 'No current author profile. Open to create one or add from the system library.'}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
              {slot.currentName && (
                <Chip size="small" variant="outlined" label={slot.currentName} />
              )}
              <Chip size="small" variant="outlined" label={slot.previewAspectLabel} />
              <Chip
                size="small"
                variant="outlined"
                label={slot.references > 0
                  ? `${slot.references} refs`
                  : `${slot.inheritedReferences} inherited refs`}
              />
              <Chip size="small" variant="outlined" label={`${slot.profileCount} variants`} />
            </Stack>
          </Box>
        ))}
      </Box>

      <SDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={`Edit ${profileOption.label}`}
        maxWidth="xl"
        fullWidth
        fullScreen
        data-testid={`platform-${platformId}-${profileKey}-design-slot-dialog`}
        primaryAction={{
          label: 'Save profile',
          onClick: handleSave,
          loading: saving,
          disabled: uploadingRefIndex !== null,
          startIcon: <SaveIcon />,
          'data-testid': `platform-${platformId}-${profileKey}-design-save`,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 1.5 }}
          data-testid={`platform-${platformId}-${profileKey}-design-iobar`}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Import / export the prompt-relevant design system as JSON.
          </Typography>
          {profileKey === 'design' && (
            <Tooltip title="Preview and copy the base palette into post-type profiles.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PaletteOutlinedIcon />}
                  onClick={() => setBaseColorsOpen(true)}
                  disabled={saving || loadingLibrary}
                  data-testid={`platform-${platformId}-apply-base-colors`}
                >
                  Apply base colors
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Export JSON: download a file and copy to clipboard.">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleExportDesignSystem}
                disabled={saving}
                data-testid={`platform-${platformId}-${profileKey}-design-export`}
              >
                Export
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Import JSON: paste from clipboard or open a file.">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileUploadOutlinedIcon />}
                onClick={(event) => setImportAnchor(event.currentTarget)}
                disabled={saving}
                data-testid={`platform-${platformId}-${profileKey}-design-import`}
              >
                Import
              </Button>
            </span>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={handleFileSelected}
            data-testid={`platform-${platformId}-${profileKey}-design-file-input`}
          />
        </Stack>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Profile name"
            value={editingProfileName}
            onChange={(event) => setEditingProfileName(event.target.value)}
            fullWidth
            disabled={saving}
          />
          <TextField
            label="Profile description"
            value={editingProfileDescription}
            onChange={(event) => setEditingProfileDescription(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={saving}
          />
        </Stack>
        <BrandStyleKitEditor
          draft={draft}
          onChange={setDraft}
          profileKey={profileKey}
          platformId={platformId}
          disabled={saving}
          uploadingReferenceIndex={uploadingRefIndex}
          onReferenceMediaChange={handleReferenceMediaChange}
        />

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2">Package slot projection</Typography>
            <Typography variant="caption" color="text.secondary">
              The current package writes this slot into runtime design/{profileKey}. Legacy profiles remain visible for review.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setPickerOpen(true)}
            disabled={saving || loadingCatalog || catalogProfiles.length === 0}
          >
            Add profiles
          </Button>
        </Stack>

        {authorProfileRows.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <SDataTable<AuthorProfileTableRow>
              columns={authorProfileColumns}
              data={authorProfileRows}
              getRowId={(row) => row.id}
              enableSorting
              enableRowActions
              enableTopToolbar={false}
              enablePagination={false}
              muiTableBodyRowProps={({ row }) => ({
                hover: true,
                onClick: () => openEditor(row.original.sourceProfile),
                sx: { cursor: 'pointer' },
              })}
              renderRowActions={({ row }) => (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  {!row.original.current && (
                    <IconButton
                      size="small"
                      aria-label={`Make ${row.original.name} current`}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleMakeCurrent(row.original.id);
                      }}
                      disabled={saving}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    aria-label={`Edit ${row.original.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEditor(row.original.sourceProfile);
                    }}
                    disabled={saving}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            />
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            No package projection exists for {profileOption.label}. Save this slot to add it to the current design package.
          </Alert>
        )}

        {catalogProfiles.length === 0 && !loadingCatalog && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            No system presets exist for {profileOption.label}. Admins can seed or create presets in System Platforms.
          </Alert>
        )}
      </SDialog>

      <SDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={`Add ${profileOption.label} profiles`}
        maxWidth="md"
        fullWidth
        primaryAction={{
          label: 'Apply selected',
          onClick: handleAddSelectedCatalogProfiles,
          loading: saving,
          disabled: Object.values(catalogRowSelection).filter(Boolean).length === 0,
          startIcon: <AddIcon />,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Select one or more system profiles. They will be copied into this author library; the current profile stays unchanged unless no current profile exists.
          </Typography>
          {catalogProfileRows.length > 0 ? (
            <SDataTable<SystemBrandStyleKitProfileRow>
              columns={catalogProfileColumns}
              data={catalogProfileRows}
              getRowId={(row) => row.id}
              enableRowSelection
              enableSorting
              enableTopToolbar={false}
              enablePagination={catalogProfileRows.length > 10}
              state={{ rowSelection: catalogRowSelection }}
              onRowSelectionChange={setCatalogRowSelection}
              muiTableBodyRowProps={() => ({
                hover: true,
                sx: { cursor: 'default' },
              })}
              initialState={{ pagination: { pageIndex: 0, pageSize: 10 } }}
            />
          ) : (
            <Alert severity="info">No system profiles exist for this scope.</Alert>
          )}
        </Stack>
      </SDialog>

      <ApplyBaseColorsDialog
        open={baseColorsOpen}
        platformId={platformId}
        baseProfile={profileKey === 'design' ? currentProfile : null}
        sets={librarySets}
        applying={baseColorsApplying}
        onClose={() => setBaseColorsOpen(false)}
        onApply={handleApplyBaseColors}
      />

      <Popover
        open={Boolean(importAnchor)}
        anchorEl={importAnchor}
        onClose={() => setImportAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuList dense>
          <MenuItem
            onClick={handleImportFromClipboard}
            data-testid={`platform-${platformId}-${profileKey}-design-import-clipboard`}
          >
            <ContentPasteIcon fontSize="small" sx={{ mr: 1 }} />
            Paste from clipboard
          </MenuItem>
          <MenuItem
            onClick={handleImportFromFileClick}
            data-testid={`platform-${platformId}-${profileKey}-design-import-file`}
          >
            <InsertDriveFileOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Open JSON file…
          </MenuItem>
        </MenuList>
      </Popover>

      <Snackbar
        open={Boolean(transferMessage)}
        autoHideDuration={transferMessage?.severity === 'error' ? 8000 : 4500}
        onClose={() => setTransferMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {transferMessage
          ? (
            <Alert
              onClose={() => setTransferMessage(null)}
              severity={transferMessage.severity}
              variant="filled"
              sx={{ maxWidth: 480 }}
            >
              {transferMessage.text}
            </Alert>
          )
          : undefined}
      </Snackbar>
    </Stack>
  );
};

type AuthorProfileTableRow = Record<string, unknown> & {
  id: string;
  scope: string;
  name: string;
  description: string;
  current: boolean;
  references: number;
  reviewed: boolean;
  sourceProfile: AuthorDesignProfile;
};

type SystemBrandStyleKitProfileRow = Record<string, unknown> & {
  id: string;
  name: string;
  description: string;
  references: number;
  reviewed: boolean;
  sourceProfile: SystemBrandStyleKitProfile;
};

type DesignSlotRow = {
  id: string;
  label: string;
  helper: string;
  currentName: string;
  description: string;
  current: boolean;
  references: number;
  inheritedReferences: number;
  referencePreviewUrls: string[];
  previewAspectRatio: string;
  previewAspectLabel: string;
  previewWidth: number;
  previewMiniWidth: number;
  reviewed: boolean;
  profileCount: number;
};
